const TRIGGER_BUFFER_PX = 150;

// Replaces vue-infinite-loading. That library auto-detected its scroll parent by
// walking up for the first ancestor with a computed `overflow-y` of auto/scroll,
// which in this app always landed on `#app` or `.center-container` — both carry
// `overflow-x: hidden`, and CSS resolves the other axis to `auto` as a result, so
// both look like scroll parents while having no bounded height. Its trigger
// (`sentinelTop - scrollParentBottom <= distance`) was therefore satisfied
// forever and every page drained back-to-back on load. IntersectionObserver does
// no such detection: the root is stated explicitly, and with `root: null` the
// target is still clipped by every scrollable ancestor on the way up.
export default {
  // Deliberately not in data(): setupScrollSentinelObserver() runs from updated()
  // and mutates both, so reactive bookkeeping would re-trigger the render it was
  // called from and spin.
  created() {
    this._scrollSentinelObservers = [];
    this._scrollSentinelLoops = {};
  },
  mounted() {
    this.$nextTick(() => this.setupScrollSentinelObserver());
  },
  // Sentinels commonly live inside a lazy b-modal, a collapsed b-collapse, or a
  // `v-if="list.length"` wrapper, so they do not exist at mount. Re-running setup
  // on every update attaches them when they appear; it is idempotent per element.
  updated() {
    this.setupScrollSentinelObserver();
  },
  // <keep-alive> (MediaArchive wraps its router-view in one) skips beforeDestroy,
  // so pause on deactivate and re-attach on activate.
  activated() {
    this.$nextTick(() => this.setupScrollSentinelObserver());
  },
  deactivated() {
    this.teardownScrollSentinelObserver();
  },
  beforeDestroy() {
    this.teardownScrollSentinelObserver();
  },
  methods: {
    // A ref on a component (b-container, b-row, …) resolves to the vm, not the
    // DOM node — IntersectionObserver and getBoundingClientRect both need the
    // element.
    resolveScrollSentinelEl(refName) {
      const target = this.$refs[refName];
      if (!target) return null;
      return target.$el || target;
    },
    // Override in components with more than one paginated list, a bounded scroll
    // container, or a reverse (prepend-older) list. Descriptor fields:
    //   ref       - $refs key of the 1px sentinel div (required)
    //   loadMore  - the fetch function (required)
    //   root      - $refs key of a bounded scroll container; omit for the viewport
    //   direction - 'bottom' (default) or 'top' for reverse/prepend lists
    // The default keeps the original single-sentinel/viewport/this.loadMore shape.
    scrollSentinelConfig() {
      return [{ ref: 'scrollSentinel', loadMore: this.loadMore }];
    },
    dropScrollSentinelObserver(entry) {
      entry.observer.disconnect();
      this._scrollSentinelObservers = this._scrollSentinelObservers
        .filter((item) => item !== entry);
    },
    setupScrollSentinelObserver() {
      this.scrollSentinelConfig().forEach((descriptor) => {
        const el = this.resolveScrollSentinelEl(descriptor.ref);
        const existing = this._scrollSentinelObservers
          .find((entry) => entry.ref === descriptor.ref);

        if (!el) {
          // v-if removed the sentinel (Network toggles its received/sent lists
          // this way) — drop the observer instead of leaving it bound to a
          // detached node for the rest of the component's life.
          if (existing) this.dropScrollSentinelObserver(existing);
          return;
        }
        if (existing) {
          if (existing.el === el) return;
          // v-if remounted the sentinel — drop the stale observer and re-observe.
          this.dropScrollSentinelObserver(existing);
        }

        const root = descriptor.root
          ? this.resolveScrollSentinelEl(descriptor.root)
          : null;
        const reverse = descriptor.direction === 'top';
        const rootMargin = reverse
          ? `${TRIGGER_BUFFER_PX}px 0px 0px 0px`
          : `0px 0px ${TRIGGER_BUFFER_PX}px 0px`;

        const observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) this.fillScrollSentinel(descriptor);
        }, { root, rootMargin, threshold: 0 });
        observer.observe(el);

        this._scrollSentinelObservers.push({ ref: descriptor.ref, el, observer });
      });
    },
    teardownScrollSentinelObserver() {
      this._scrollSentinelObservers.forEach(({ observer }) => observer.disconnect());
      this._scrollSentinelObservers = [];
    },
    // IntersectionObserver only re-fires on a change of intersection state, not
    // every time the target's geometry changes. A search/filter reset can clear
    // the list while the sentinel stays visible the whole time (nothing to
    // "cross" a threshold), so no new fire happens and pagination silently
    // stalls. Call this after resetting a list's page/hasNextPage/items to force
    // a re-check regardless of current visibility. Omit `ref` to re-check every
    // sentinel. Safe to call any time — it's a no-op past the sentinel's trigger
    // buffer and for sentinels that aren't rendered, and fillScrollSentinel's own
    // flag makes it idempotent with an observer firing at the same moment.
    recheckScrollSentinel(ref) {
      this.scrollSentinelConfig()
        .filter((descriptor) => ref === undefined || descriptor.ref === ref)
        .forEach((descriptor) => this.fillScrollSentinel(descriptor));
    },
    // Keeps loading pages while the sentinel is still within the trigger buffer,
    // so short viewports (or tall/1080p screens where one page doesn't reach the
    // fold) fill up instead of silently stalling until a scroll that never needs
    // to happen. Stops as soon as the sentinel stops moving (loadMore no-op'd:
    // no more pages, or already fetching) or it's pushed past the buffer, at
    // which point the observer will naturally re-fire on the next real scroll.
    //
    // This is not the runaway bug we replaced: geometry is re-measured every
    // iteration against a real edge, so the loop terminates. It only terminates
    // if that edge is real, though — a `root` must be a container with an actual
    // height/max-height. Pointing it at an unbounded `overflow: auto` element
    // reproduces the original bug, because the sentinel can never pass its
    // bottom. Prefer the viewport (no `root`) whenever the container is not
    // reliably bounded.
    async fillScrollSentinel(descriptor) {
      const { ref, loadMore } = descriptor;
      if (this._scrollSentinelLoops[ref]) return;
      this._scrollSentinelLoops[ref] = true;
      const reverse = descriptor.direction === 'top';
      const rootFor = () => (descriptor.root
        ? this.resolveScrollSentinelEl(descriptor.root)
        : null);
      try {
        for (;;) {
          const el = this.resolveScrollSentinelEl(ref);
          // getBoundingClientRect() on a display:none element is all zeros, and
          // so is its hidden root's, which would measure as "in view" and load a
          // page for a list the user can't see (a collapsed b-collapse, an
          // unopened b-modal). getClientRects() is empty exactly when there are
          // no layout boxes.
          if (!el || !el.getClientRects().length) break;
          const root = rootFor();
          if (descriptor.root && !root) break;

          const rect = el.getBoundingClientRect();
          if (reverse) {
            const rootTop = root ? root.getBoundingClientRect().top : 0;
            if (rect.bottom < rootTop - TRIGGER_BUFFER_PX) break;
          } else {
            const rootBottom = root
              ? root.getBoundingClientRect().bottom
              : window.innerHeight;
            if (rect.top > rootBottom + TRIGGER_BUFFER_PX) break;
          }
          const before = rect.top;

          // Reverse lists prepend, which shifts every existing row down by the
          // height of the new block. Restore the scroll offset so the message
          // the user was reading stays put — vue-infinite-loading did this
          // internally (its scrollBarStorage) and nothing else in the app does.
          const heightBefore = reverse && root ? root.scrollHeight : null;
          await loadMore();
          await this.$nextTick();
          if (reverse && root && heightBefore !== null) {
            root.scrollTop += root.scrollHeight - heightBefore;
          }

          const elAfter = this.resolveScrollSentinelEl(ref);
          if (!elAfter || !elAfter.getClientRects().length) break;
          const after = elAfter.getBoundingClientRect().top;
          if (after === before) break;
        }
      } finally {
        this._scrollSentinelLoops[ref] = false;
      }
    },
  },
};
